import { useAutoAnimate } from "@formkit/auto-animate/react"
import { useContext, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { ERRORTYPES } from "../../config"
import { getEnvConfig } from "../../env"
import { Context } from "../../store/store"
import Alert from "../elements/Alert"
import Spinner from "../elements/Spinner"
import PreFillForm from "./PreFillForm"
import { useForm } from "react-hook-form"
import axios from "axios"
import clsx from "clsx"
import ElemInput from "../elements/Input"
import ElemCheckbox from "../elements/Checkbox"

const Form = () => {
	const { globalState } = useContext(Context)
	const navigate = useNavigate()
	const [formError, setFormError] = useState<ERRORTYPES | null>(null)
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [animationRef] = useAutoAnimate<HTMLDivElement>()

	const {
		register,
		handleSubmit,
		setError,
		setValue,
		watch,
		reset,
		formState: { errors },
	} = useForm({})

	function handleFormSubmit(data) {
		if (!globalState.receiptReference) {
			setFormError(ERRORTYPES.UPLOADSMISSING)
			return
		}

		setIsLoading(true)
		const env = getEnvConfig()

		axios
			.post(env.apibase + "order.php?cv=" + Date.now(), {
				...data,
				receiptDataEntries: globalState.receiptReference,
			})
			.then(res => {
				if (res.data.externalIdentifier) {
					try {
						// eslint-disable-next-line
						let dataLayer = window["dataLayer"] || []
						dataLayer.push({
							event: "form-submit",
							"form-name": "particpate",
							"form-submit": false,
							field_error: "send",
						})
					} catch (e) {}

					navigate("/danke")
				} else {
					setFormError(ERRORTYPES.SERVER)
					try {
						// eslint-disable-next-line
						let dataLayer = window["dataLayer"] || []
						dataLayer.push({
							event: "form-submit",
							"form-name": "participate",
							"form-submit": true,
							package: globalState.package,
						})
					} catch (e) {}
				}
			})
			.catch(error => {
				const serverChildren = error?.response?.data?.errors?.children
				if (serverChildren) {
					const fieldMap: Record<string, string> = {
						firstName: "firstName",
						lastName: "lastName",
						addressStreet: "street",
						addressHouseNumber: "houseNr",
						addressZip: "zip",
						addressCity: "city",
						email: "email",
						optInTerms: "optInTerms",
						optInPrivacy: "optInPrivacy",
						birthdate: "birthday",
					}
					let hasFieldErrors = false
					Object.entries(fieldMap).forEach(([serverField, formField]) => {
						const fieldErrors = serverChildren[serverField]?.errors
						if (fieldErrors?.length) {
							setError(formField as any, { type: "server", message: fieldErrors.join(" ") })
							hasFieldErrors = true
						}
					})
					if (!hasFieldErrors) {
						setFormError(ERRORTYPES.SERVER)
					}
				} else {
					setFormError(ERRORTYPES.SERVER)
				}
			})
			.finally(() => {
				setIsLoading(false)
			})
	}

	return (
		<>
			<form
				onSubmit={handleSubmit(handleFormSubmit)}
				noValidate={true}
				className="mx-auto max-w-[794px]  w-full pt-[50px] sm:pt-0"
			>
				<div className="grid grid-cols-1 relative">
					<img
						src={"/images/personalData.png"}
						alt="Schritt 3: Persönliche Daten eingeben"
						className="absolute left-auto right-0 md:right-auto left-0 top-0 w-full  max-w-[118px] md:max-w-[237px] -translate-y-[80%]  md:-translate-y-1/2  md:-translate-x-2/3 "
					/>
					<svg
						width="65"
						height="51"
						viewBox="0 0 65 51"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
						className="absolute -top-[60px] right-[110px] sm:hidden"
					>
						<mask id="mask0_6254_720" maskUnits="userSpaceOnUse" x="0" y="0" width="65" height="51">
							<path
								d="M-5.41496e-06 8.88664L7.15129 50.4654L64.4702 41.941L57.3189 0.362243L-5.41496e-06 8.88664Z"
								fill="white"
							/>
						</mask>
						<g mask="url(#mask0_6254_720)">
							<path
								d="M5.19657 19.328C5.67246 19.1412 6.22356 19.2711 6.55831 19.6501L11.3282 24.9893C16.4405 16.5561 25.2409 9.79404 36.8889 5.30234C46.1397 1.73215 53.8162 0.88311 54.2121 0.864578L56.3512 0.763376C57.0092 0.7311 57.5125 1.15567 57.5551 1.79505L57.7015 4.003C57.7348 4.52775 57.4335 5.00639 56.9163 5.16402C56.6998 5.23153 35.3679 12.0412 24.1469 31.8555L32.2645 34.0534C32.7212 34.1772 33.0711 34.5539 33.1448 35.0121C33.2184 35.4703 33.0055 35.9207 32.6013 36.1624L19.8392 43.8214L8.75027 50.1469C8.5852 50.2421 8.39736 50.2952 8.21201 50.3026C8.00139 50.3138 7.78651 50.27 7.59351 50.1726C7.22435 49.9854 6.98145 49.628 6.93905 49.2308L5.78271 36.781L4.47113 20.4751C4.43202 19.9764 4.72573 19.514 5.19657 19.328Z"
								fill="#005E34"
							/>
						</g>
					</svg>

					<div>
						<div className="bg-white px-[20px] sm:px-[80px] py-[40px] grid grid-cols-1 gap-5">
							<PreFillForm setValue={setValue} />
							<div className="grid sm:grid-cols-2 grid-cols-1 gap-4">
								<ElemInput
									label={"Vorname *"}
									hasError={errors.firstName}
									errMsg={"Bitte Vorname angeben."}
									value={watch("firstName", false)}
									required={true}
								>
									<input
										{...register("firstName", {
											required: true,
											minLength: 1,
											maxLength: 30,
											pattern:
												/^(?!mailto:|https:\/\/|http:\/\/|ftp:\/\/|sftp:\/\/)[a-zA-ZÀ-ÖØ-öø-ÿu~áçíóôąćČčđėěğīİıĶŁłńŕśŞşŠšūźżŽž̈БКМСавгжиклнорстьіẞ–—\-\s]+$/,
										})}
										type="text"
										minLength={1}
										maxLength={30}
										autoComplete="off"
										className={clsx(
											"peer inputElem",
											errors.firstName && "inputElemErr js-formerror"
										)}
									/>
								</ElemInput>
								<ElemInput
									label={"Nachname *"}
									hasError={errors.lastName}
									errMsg={"Bitte Nachname angeben."}
									value={watch("lastName", false)}
									required={true}
								>
									<input
										{...register("lastName", {
											required: true,
											minLength: 1,
											maxLength: 30,
											pattern:
												/^(?!mailto:|https:\/\/|http:\/\/|ftp:\/\/|sftp:\/\/)[a-zA-ZÀ-ÖØ-öø-ÿu~áçíóôąćČčđėěğīİıĶŁłńŕśŞşŠšūźżŽž̈БКМСавгжиклнорстьіẞ–—\-\s]+$/,
										})}
										type="text"
										minLength={1}
										maxLength={30}
										autoComplete="off"
										className={clsx(
											"peer inputElem",
											errors.lastName && "inputElemErr js-formerror"
										)}
									/>
								</ElemInput>
							</div>

							<div className="grid sm:grid-cols-[1fr_120px] grid-cols-1 gap-4">
								<ElemInput
									label={"Straße *"}
									hasError={errors.street}
									errMsg={"Bitte Straße angeben."}
									value={watch("street", false)}
									required={true}
								>
									<input
										{...register("street", {
											required: true,
											minLength: 1,
											maxLength: 240,
											pattern:
												/^[a-zA-ZÀ-ÖØ-öø-ÿ0-9 "#&()*+,-.\\\/:?@_u|~áçíóôąćČčđėěğīİıĶŁłńŕśŞşŠšūźżŽž̈БКМСавгжиклнорстьіẞ–—‘’'']+$/,
										})}
										type="text"
										minLength={1}
										maxLength={240}
										autoComplete="off"
										className={clsx("peer inputElem", errors.street && "inputElemErr js-formerror")}
									/>
								</ElemInput>
								<ElemInput
									label={"Hausnummer *"}
									hasError={errors.houseNr}
									errMsg={"Bitte Hausnummer angeben."}
									value={watch("houseNr", false)}
									required={true}
								>
									<input
										{...register("houseNr", {
											required: true,
											minLength: 1,
											maxLength: 14,
											pattern:
												/^[a-zA-ZÀ-ÖØ-öø-ÿ0-9 "#&()*+,-.\\\/:?@_u|~áçíóôąćČčđėěğīİıĶŁłńŕśŞşŠšūźżŽž̈БКМСавгжиклнорстьіẞ–—‘’'']+$/,
										})}
										type="text"
										minLength={1}
										maxLength={14}
										autoComplete="off"
										className={clsx(
											"peer inputElem",
											errors.houseNr && "inputElemErr js-formerror"
										)}
									/>
								</ElemInput>
							</div>

							<div className="grid sm:grid-cols-[120px_1fr] grid-cols-1 gap-4">
								<ElemInput
									label={"Postleitzahl *"}
									hasError={errors.zip}
									errMsg={"Bitte Postleitzahl angeben."}
									value={watch("zip", false)}
									required={true}
								>
									<input
										{...register("zip", {
											required: true,
											pattern: /^\d{5}$/,
											minLength: 5,
											maxLength: 5,
										})}
										type="text"
										minLength={5}
										maxLength={5}
										autoComplete="off"
										className={clsx("peer inputElem", errors.zip && "inputElemErr js-formerror")}
									/>
								</ElemInput>
								<ElemInput
									label={"Ort *"}
									hasError={errors.city}
									errMsg={"Bitte Ort angeben."}
									value={watch("city", false)}
									required={true}
								>
									<input
										{...register("city", {
											required: true,
											minLength: 1,
											maxLength: 240,
											pattern:
												/^[a-zA-ZÀ-ÖØ-öø-ÿ0-9 "#&()*+,-.\\\/:?@_u|~áçíóôąćČčđėěğīİıĶŁłńŕśŞşŠšūźżŽž̈БКМСавгжиклнорстьіẞ–—‘’'']+$/,
										})}
										type="text"
										minLength={1}
										maxLength={240}
										autoComplete="off"
										className={clsx("peer inputElem", errors.city && "inputElemErr js-formerror")}
									/>
								</ElemInput>
							</div>

							<div className="grid sm:grid-cols-[160px_1fr] grid-cols-1 gap-4">
								<ElemInput
									label={"Geburtstag *"}
									hasError={errors.birthday}
									errMsg={"Eine Teilnahme ist erst ab 18 Jahren möglich."}
									value={watch("birthday", false)}
									required={true}
								>
									<input
										{...register("birthday", {
											required: true,
											validate: value => {
												const today = new Date()
												const birthDate = new Date(value)
												let age = today.getFullYear() - birthDate.getFullYear()
												const m = today.getMonth() - birthDate.getMonth()
												if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
													age--
												}
												return age >= 18
											},
										})}
										min="1900-01-01"
										max="2010-01-01"
										type="date"
										autoComplete="off"
										className={clsx(
											"peer inputElem",
											errors.birthday && "inputElemErr js-formerror"
										)}
									/>
								</ElemInput>
								<ElemInput
									label={"E-Mail *"}
									hasError={errors.email}
									errMsg={"Bitte E-Mail angeben."}
									value={watch("email", false)}
									required={true}
								>
									<input
										{...register("email", { required: true })}
										type="email"
										autoComplete="off"
										className={clsx("peer inputElem", errors.email && "inputElemErr js-formerror")}
									/>
								</ElemInput>
							</div>
						</div>

						<div className=" mt-5 sm:mt-10">
							<p className="font-CalibriRegular text-[12px] sm:text-[14px] text-left sm:text-center ">
								Teilnahme ab 18 Jahren. Die Gewinner werden in den ersten zwei Wochen nach
								Teilnahmeschluss ermittelt.
							</p>
							<div className="max-w-[450px] mx-auto grid grid-cols-1 gap-3 pt-5">
								<ElemCheckbox
									hasError={errors.optInTerms}
									errMsg={"Bitte akzeptiere die Teilnahmebedingungen."}
									trackingOption={[{ eventname: "double_opt_in_confirmed " }]}
									label={
										<>
											Ich habe die{" "}
											<Link
												to={"/teilnahmebedingungen"}
												className="underline text-black"
												target="_blank"
											>
												Teilnahmebedingungen
											</Link>{" "}
											gelesen und akzeptiere diese.*
										</>
									}
								>
									<input
										{...register("optInTerms", { required: true })}
										type="checkbox"
										className="w-1 h-1 opacity-0 absolute peer"
									/>
								</ElemCheckbox>
								<ElemCheckbox
									hasError={errors.optInPrivacy}
									errMsg={"Bitte akzeptiere die Datenschutzerklärung."}
									trackingOption={[{ eventname: "double_opt_in_confirmed " }]}
									label={
										<>
											Ich habe die{" "}
											<Link to={"/datenschutz"} className="underline text-black" target="_blank">
												Datenschutzerklärung
											</Link>{" "}
											gelesen und akzeptiere diese.*
										</>
									}
								>
									<input
										{...register("optInPrivacy", { required: true })}
										type="checkbox"
										className="w-1 h-1 opacity-0 absolute peer"
									/>
								</ElemCheckbox>

								<p className="font-CalibriRegular text-black text-[14px] text-center">* Pflichtfeld</p>
							</div>
						</div>

						{formError && (
							<Alert className={"my-4 font-bold text-errorred"}>
								{formError === ERRORTYPES.SERVER ? (
									<>
										Bei der Übertragung deiner Daten ist ein Fehler aufgetreten. Bitte versuche es
										erneut. Sollten die Probleme weiterhin bestehen, kannst Du Dich an unser
										Service-Team wenden. Du erreichst uns per E-Mail unter{" "}
										<a href="mailto:bulldog@gewinnspiel-2026.de" className="underline">
											bulldog@gewinnspiel-2026.de
										</a>
										. Bitte entschuldige die Unannehmlichkeiten.
									</>
								) : (
									formError
								)}
							</Alert>
						)}
						<div className="pb-2 pt-8 text-center" ref={animationRef}>
							<img
								src={"/images/bulldog.png"}
								alt="Bulldog Produkte"
								className="mx-auto block max-w-[220px]  "
							/>

							<button type="submit" className="btn btn-green" disabled={isLoading ? true : undefined}>
								{isLoading && <Spinner />}
								Jetzt teilnehmen!
							</button>
						</div>
					</div>
				</div>
			</form>
		</>
	)
}

export default Form
